import { Box, Container, GridLegacy as Grid, Grow, Typography, useTheme } from '@mui/material';
import { CinemaModel, useGetCinemaList } from '@/logic/services/cinemas';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { handleError, is500 } from '@/logic/shared';

import { AxiosError } from 'axios';
import React from 'react';
import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';

import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import CardComponent from '@/components/shared/card-component';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const emptyCinemaImgCard = '/icons/cinema/emptyCinemaImgCard.svg';

export const Cinema: NextPage<{
  serverCityId: number;
  path: string;
}> = ({ serverCityId, path }) => {
  const theme = useTheme();
  const intl = useIntl();
  const { cityId: clientCityId } = useCity();

  const cityId = clientCityId || serverCityId;

  const { data: pageData } = useGetCinemaList({
    params: { cityId },
  });
  const { data: seoDataList, isLoading } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(seoDataList?.pageMetaData, SeoPagesEnum.cinemas);

  return (
    <>
      <Seo
        title={
          seoPage?.titleTag ||
          intl.formatMessage({
            id: 'cinema',
          })
        }
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={seoPage?.descriptionTag}
        hasFollow={seoPage?.hasFollow}
        hasIndex={seoPage?.hasIndex}
        components={() => (
          <script // hubspot new script
            type='text/javascript'
            id='hs-script-loader'
            async
            defer
            src='//js-eu1.hs-scripts.com/143790225.js'
          ></script>
        )}
      />
      <Container
        component='section'
        maxWidth='muviFixedContainer'
        sx={{
          paddingX: {
            mobile: '16px',
            tablet: '30px',
            mediumDesktop: '90px',
          },
          marginBottom: pxToRem(122),
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: pxToRem(30),
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='cinema' />
        </Typography>
        {!isLoading ? (
          <Box>
            <Grid
              container
              rowSpacing={{
                mobile: '16px',
                tablet: pxToRem(48),
              }}
              columnSpacing={{
                mobile: '0px',
                tablet: pxToRem(46),
              }}
            >
              {pageData?.cinema.map((cinema: CinemaModel, index: number) => (
                <Grow
                  in
                  style={{
                    transformOrigin: '0 0 0',
                  }}
                  {...{ timeout: 1000 + index * 500 }}
                  key={cinema.id}
                >
                  <Grid item mobile={12} tablet={6} laptop={4}>
                    <CardComponent
                      image={cinema.image || emptyCinemaImgCard}
                      name={cinema.name}
                      description={cinema.description}
                      slug={cinema.uniqueName}
                      cityName={cinema?.city?.name}
                      address1={cinema.address1}
                      linkName='cinemas'
                      alt={cinema?.imageAlt || cinema?.name}
                    />
                  </Grid>
                </Grow>
              ))}
            </Grid>
          </Box>
        ) : (
          <MovieLoaderWrapper
            innerBox={{
              width: '160px',
              height: '160px',
              [theme.breakpoints.down('laptop')]: {
                width: '100px',
                height: '100px',
              },
            }}
            outerBox={{
              width: '100%',
              height: {
                mobile: 'calc(100vh - 56px)',
                tablet: 'calc(100vh - 100px)',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const cityId = Number(context.req.cookies.city);

  if (!cityId) {
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=60, stale-while-revalidate=300'
    );
  }

  const queryClient = new QueryClient();

  const [cinemasResult, seoMetaResult, intlMessages] = await Promise.all([
    useGetCinemaList
      // `Number(undefined)` is NaN for a visitor with no city cookie, which
      // went out as `cityId=NaN`. The API happens to treat it as no filter
      // (verified: 91 cinemas, same as omitting it), but it is an accident.
      // Send the param only when there is a real city, as index.tsx does.
      .fetchQuery(queryClient, { params: { ...(cityId && { cityId }) }, headers })
      .catch((error: unknown) => ({ error })),
    useGetPagesSeoMeta
      .fetchQuery(queryClient, { headers })
      .catch((error: unknown) => ({ error })),
    loadIntlMessages(locale as string, defaultLocale),
  ]);

  for (const result of [cinemasResult, seoMetaResult]) {
    if (result && typeof result === 'object' && 'error' in result && is500(result.error)) {
      return handleError(result.error as AxiosError);
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      serverCityId: cityId,
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages,
    },
  };
};

export default Cinema;
